/** mixed-engine Amazons の serde 形。 index = row * 10 + col、白 First が先手。 */
export type Side = 'First' | 'Second';
export type AmazonsCell = 0 | 1 | 2 | 3;

export interface AmazonsMove {
  from: number;
  to: number;
  arrow: number;
}

export interface AmazonsRegion {
  owner: Side;
  cells: number[];
  amazons: number[];
  moves: number;
  upper: number;
  exact: boolean;
  line: AmazonsMove[];
}

export interface AmazonsTerritory {
  to_move: Side;
  first_moves: number;
  first_upper: number;
  second_moves: number;
  second_upper: number;
  regions: AmazonsRegion[];
}

export interface AmazonsOutcome {
  winner: Side;
  kind: 'NoMoves' | 'Territory';
  territory?: AmazonsTerritory | null;
}

export interface AmazonsState {
  board: AmazonsCell[];
  to_move: Side;
  ply: number;
  last_move?: AmazonsMove | null;
  outcome: AmazonsOutcome | null;
}

export interface AmazonsSelection {
  from: number;
  to: number | null;
}

export const AMAZONS_SIZE = 10;

export type AmazonsTerritoryOwner = Side | 'contested' | 'unreachable' | 'blocked';

/** Same conservative separation test as engine amazons::adjudicate/components:
 * arrows are permanent walls; empty squares and both colors' amazons form
 * 8-neighbor components for potential future reachability (amazons may move).
 * Occupied cells themselves are displayed as unreachable, never territory.
 * A component with both colors remains contested,
 * regardless of which amazon is closer. Ownership does not assert exact moves.
 */
export function amazonsTerritoryOwners(board: readonly AmazonsCell[]): AmazonsTerritoryOwner[] {
  const owners: AmazonsTerritoryOwner[] = Array(AMAZONS_SIZE * AMAZONS_SIZE).fill('blocked');
  const visited = new Set<number>();
  for (let start = 0; start < owners.length; start++) {
    if (board[start] === 3 || board[start] == null || visited.has(start)) continue;
    const component = [start];
    visited.add(start);
    let white = false;
    let black = false;
    for (let cursor = 0; cursor < component.length; cursor++) {
      const square = component[cursor];
      white ||= board[square] === 1;
      black ||= board[square] === 2;
      const row = Math.floor(square / AMAZONS_SIZE);
      const col = square % AMAZONS_SIZE;
      for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        const r = row + dr;
        const c = col + dc;
        if (r < 0 || r >= AMAZONS_SIZE || c < 0 || c >= AMAZONS_SIZE) continue;
        const next = r * AMAZONS_SIZE + c;
        if (visited.has(next) || board[next] === 3 || board[next] == null) continue;
        visited.add(next);
        component.push(next);
      }
    }
    const owner = white && black ? 'contested' : white ? 'First' : black ? 'Second' : 'unreachable';
    for (const square of component) owners[square] = board[square] === 0 ? owner : 'unreachable';
  }
  return owners;
}

export interface AmazonsRemainingSummary {
  cells: number;
  maximum: number;
  exact: boolean;
}

/** Each full move burns one empty square. Potential component space is a safe
 * upper bound, never an established maximum. Completed separation proofs can
 * tighten it, and only equal lower/upper establishes the exact maximum.
 */
export function amazonsRemainingSummaries(state: AmazonsState): Record<Side, AmazonsRemainingSummary> {
  const owners = amazonsTerritoryOwners(state.board);
  const counts = { First: 0, Second: 0 };
  state.board.forEach((cell, square) => {
    if (cell !== 0) return;
    if (owners[square] === 'First' || owners[square] === 'contested') counts.First++;
    if (owners[square] === 'Second' || owners[square] === 'contested') counts.Second++;
  });
  const proof = state.outcome?.territory;
  return {
    First: { cells: counts.First, maximum: proof?.first_upper ?? counts.First, exact: proof ? proof.first_moves === proof.first_upper : counts.First === 0 },
    Second: { cells: counts.Second, maximum: proof?.second_upper ?? counts.Second, exact: proof ? proof.second_moves === proof.second_upper : counts.Second === 0 },
  };
}

export function squareName(square: number): string {
  return `${String.fromCharCode(97 + (square % AMAZONS_SIZE))}${Math.floor(square / AMAZONS_SIZE) + 1}`;
}

export function moveNotation(move: AmazonsMove): string {
  return `${squareName(move.from)} → ${squareName(move.to)} / 矢 ${squareName(move.arrow)}`;
}

export function sideLabel(side: Side): string {
  return side === 'First' ? '白' : '黒';
}

/** 合法手そのものを返す。UI は queen-ray 等の独自合法性判定をしない。 */
export function selectAmazonsSquare(
  legal: readonly AmazonsMove[],
  selection: AmazonsSelection | null,
  square: number,
): { selection: AmazonsSelection | null; move?: AmazonsMove; invalid?: boolean } {
  const isSource = legal.some((move) => move.from === square);
  if (!selection) {
    return isSource ? { selection: { from: square, to: null } } : { selection, invalid: true };
  }
  if (selection.to !== null) {
    const move = legal.find(
      (candidate) => candidate.from === selection.from && candidate.to === selection.to && candidate.arrow === square,
    );
    // 移動元も合法な矢先になり得る。再選択より先に矢の判定を行う。
    if (move) return { selection: null, move };
    if (square === selection.to) return { selection: { from: selection.from, to: null } };
    if (isSource) return { selection: { from: square, to: null } };
    return { selection, invalid: true };
  }
  if (square === selection.from) return { selection: null };
  if (isSource) return { selection: { from: square, to: null } };
  if (legal.some((move) => move.from === selection.from && move.to === square)) {
    return { selection: { from: selection.from, to: square } };
  }
  return { selection, invalid: true };
}

/** 矢確定前の移動プレビュー。正本 state の盤は変更しない。 */
export function previewAmazonsBoard(board: readonly AmazonsCell[], selection: AmazonsSelection | null): AmazonsCell[] {
  const preview = [...board];
  if (selection?.to != null) {
    preview[selection.to] = board[selection.from];
    preview[selection.from] = 0;
  }
  return preview;
}

/** 完全分離区画の証明手順をプレビューする。別区画・正本 state は保全。 */
export function territoryProofBoard(board: readonly AmazonsCell[], region: AmazonsRegion, step: number): AmazonsCell[] {
  const preview = [...board];
  for (const move of region.line.slice(0, Math.max(0, Math.min(step, region.line.length)))) {
    preview[move.to] = preview[move.from];
    preview[move.from] = 0;
    preview[move.arrow] = 3;
  }
  return preview;
}

export function regionBoundsLabel(region: AmazonsRegion): string {
  return region.exact
    ? `最長 ${region.moves} 手（確定）`
    : `最長 ≤${region.upper} 手（未確定）`;
}

/** 手番による同手数時の敗者を含め、エンジンが勝敗を証明した不等式を説明する。 */
export function territoryReason(outcome: AmazonsOutcome): string {
  const territory = outcome.territory;
  if (!territory) return '両者の陣地が完全に分離し、残り手数で勝敗が確定しました。';
  const firstToMove = territory.to_move === 'First';
  const ownLower = firstToMove ? territory.first_moves : territory.second_moves;
  const ownUpper = firstToMove ? territory.first_upper : territory.second_upper;
  const otherLower = firstToMove ? territory.second_moves : territory.first_moves;
  const otherUpper = firstToMove ? territory.second_upper : territory.first_upper;
  const own = sideLabel(territory.to_move);
  const other = firstToMove ? '黒' : '白';
  return outcome.winner === territory.to_move
    ? `手番の${own}は最低 ${ownLower} 手、${other}は最大 ${otherUpper} 手。${ownLower} > ${otherUpper} のため${own}が最後まで指せます。`
    : `手番の${own}は最大 ${ownUpper} 手、${other}は最低 ${otherLower} 手。${ownUpper} ≤ ${otherLower} のため、先に手番が来る${own}が指せなくなります。`;
}
